import React from 'react'
import { FaClock, FaPalette, FaPlay, FaTrophy, FaUsers } from 'react-icons/fa'
import { useNavigate } from 'react-router'

import { useWebRTC } from '../library/web-rtc/useWebRTC'

export function LandingPage() {
  const webRTC = useWebRTC()
  const navigate = useNavigate()

  React.useEffect(() => {
    const handleSessionCreated = (event: { sessionId: string }) => {
      navigate(`/party/${event.sessionId}`)
    }

    webRTC.signaling.on('session-created', handleSessionCreated)

    return () => {
      webRTC.signaling.off('session-created', handleSessionCreated)
    }
  }, [webRTC])

  return (
    <div className="min-h-screen gradient-bg">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center min-h-screen px-2 sm:px-4 py-4 sm:py-8 md:py-12">
        <div className="text-center max-w-4xl mx-auto animate-scale-in">
          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-3 sm:mb-4 md:mb-6 drop-shadow-2xl">
            🎨 <span className="rainbow-gradient-text">Shade Parades</span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl mb-6 sm:mb-8 md:mb-12 font-light px-2">
            Can you match the color? Test your perception in this exciting multiplayer challenge!
          </p>

          {/* CTA Button */}
          <button
            onClick={() => webRTC.createHostSession({ name: 'Host' })}
            className="btn-primary text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-5 mb-8 sm:mb-12 md:mb-16 animate-bounce-subtle inline-flex items-center gap-2 sm:gap-3 text-white"
          >
            <FaPlay /> Start a Party
          </button>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mt-6 sm:mt-8 md:mt-12">
            <div className="card text-center transform hover:-translate-y-2 transition-transform duration-300">
              <div className="inline-block p-2 sm:p-3 md:p-4 rainbow-gradient rounded-full mb-2 sm:mb-3 md:mb-4">
                <FaPalette className="text-2xl sm:text-3xl md:text-4xl text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Multiple Pickers</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600">Experience different color picker types each round - from basic to advanced!</p>
            </div>

            <div className="card text-center transform hover:-translate-y-2 transition-transform duration-300">
              <div className="inline-block p-2 sm:p-3 md:p-4 rainbow-gradient rounded-full mb-2 sm:mb-3 md:mb-4">
                <FaClock className="text-2xl sm:text-3xl md:text-4xl text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Fast-Paced</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600">Quick rounds keep the energy high. One click to submit your guess!</p>
            </div>

            <div className="card text-center transform hover:-translate-y-2 transition-transform duration-300 sm:col-span-2 md:col-span-1">
              <div className="inline-block p-2 sm:p-3 md:p-4 rainbow-gradient rounded-full mb-2 sm:mb-3 md:mb-4">
                <FaTrophy className="text-2xl sm:text-3xl md:text-4xl text-white" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Smart Scoring</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600">Get points based on how close your guess is. Perfect matches get full points!</p>
            </div>
          </div>

          {/* How to Play */}
          <div className="card mt-6 sm:mt-8 md:mt-12 text-left">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-6 md:mb-8 rainbow-gradient-text">How to Play</h2>
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full rainbow-gradient text-white flex items-center justify-center font-bold text-sm sm:text-base">
                  1
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-0.5 sm:mb-1 text-sm sm:text-base">Start a Party</h4>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">Click "Start a Party" to create a game room and share the link with friends.</p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full rainbow-gradient text-white flex items-center justify-center font-bold text-sm sm:text-base">
                  2
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-0.5 sm:mb-1 text-sm sm:text-base">Configure & Play</h4>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">Choose the number of rounds, time limits, and color picker types.</p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full rainbow-gradient text-white flex items-center justify-center font-bold text-sm sm:text-base">
                  3
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-0.5 sm:mb-1 text-sm sm:text-base">Match the Color</h4>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">Each round, a target color is shown. Try to match it by clicking on the color picker!</p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
                <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full rainbow-gradient text-white flex items-center justify-center font-bold text-sm sm:text-base">
                  4
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-0.5 sm:mb-1 text-sm sm:text-base">Win!</h4>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">Score points based on accuracy. The closest guess wins the most points!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-6 sm:mt-8 md:mt-12 text-gray-600 text-xs sm:text-sm">
            <p className="flex items-center justify-center gap-1 sm:gap-2">
              <FaUsers /> Supports 2+ players • Real-time multiplayer
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
